import { useEffect, useMemo, useRef, useState } from "react";
import {
  Video, Square, Upload, Loader2, Play, Sparkles, AlertCircle, Film,
  ListChecks, Plus, Trash2, GripVertical, X, ChevronRight, CheckCircle2,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { api, APP_ORIGIN } from "@/lib/api";
import { appConfirm } from "@/components/AppDialogHost";

/**
 * Academy Studio Panels (Update #133):
 *   1. <ScreenRecorderPanel />     — getDisplayMedia capture → Whisper → Claude tutorial script
 *   2. <SoraVideoGeneratorPanel /> — text-to-video via Sora 2 (EMERGENT_LLM_KEY)
 *   3. <LearningPathsPanel />      — group tutorials into ordered learning paths + progress
 */


// ═══════════════════════════════════════════════════════════════════
// 1. Screen Recorder
// ═══════════════════════════════════════════════════════════════════

const RECORDER_MIME_CANDIDATES = [
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
  "video/mp4",
];

function pickSupportedMime() {
  if (typeof MediaRecorder === "undefined") return null;
  for (const m of RECORDER_MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return null;
}

export function ScreenRecorderPanel({ tutorialId, existingRecording, onUploaded }) {
  const [state, setState] = useState("idle");   // idle | recording | preparing | uploading | done
  const [error, setError] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [blob, setBlob] = useState(null);
  const [duration, setDuration] = useState(0);
  const [uploadedInfo, setUploadedInfo] = useState(null);
  const [captureAudio, setCaptureAudio] = useState(true);

  const chunksRef = useRef([]);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const startedAtRef = useRef(0);
  const timerRef = useRef(null);

  const supported = typeof navigator !== "undefined"
    && !!navigator.mediaDevices?.getDisplayMedia
    && typeof MediaRecorder !== "undefined";

  const start = async () => {
    setError("");
    setPreviewUrl("");
    setBlob(null);
    chunksRef.current = [];
    try {
      // Ask for the screen (+ system audio if the browser will allow it)
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 30 } },
        audio: captureAudio,
      });
      // Additionally grab the mic so voiceovers land in the same file
      let micStream = null;
      if (captureAudio) {
        try {
          micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch {
          // No mic permission — still record video + system audio
        }
      }
      // Mix mic audio in with the display audio
      const audioTracks = [
        ...displayStream.getAudioTracks(),
        ...(micStream ? micStream.getAudioTracks() : []),
      ];
      const combined = new MediaStream([
        ...displayStream.getVideoTracks(),
        ...audioTracks,
      ]);
      streamRef.current = combined;

      const mime = pickSupportedMime();
      if (!mime) throw new Error("Browser doesn't support screen recording (MediaRecorder mime).");

      const rec = new MediaRecorder(combined, {
        mimeType: mime,
        videoBitsPerSecond: 2_500_000,
      });
      recorderRef.current = rec;

      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const merged = new Blob(chunksRef.current, { type: mime.split(";")[0] });
        chunksRef.current = [];
        setBlob(merged);
        setPreviewUrl(URL.createObjectURL(merged));
        // Stop all tracks (both display + mic)
        combined.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setState("preparing");
      };

      // Auto-stop if the user hits the browser's "stop sharing" pill
      displayStream.getVideoTracks()[0].addEventListener("ended", () => {
        if (recorderRef.current?.state !== "inactive") {
          try { recorderRef.current.stop(); } catch { /* noop */ }
        }
      });

      rec.start(1000);   // 1s chunks — keeps the buffer safe
      startedAtRef.current = performance.now();
      setState("recording");
      timerRef.current = setInterval(() => {
        setDuration(Math.floor((performance.now() - startedAtRef.current) / 1000));
      }, 500);
    } catch (e) {
      const msg = e?.name === "NotAllowedError"
        ? "Screen-share permission was denied. Click the mic/screen icon in the address bar and allow it, then try again."
        : (e?.message || "Couldn't start screen recording");
      setError(msg);
      setState("idle");
    }
  };

  const stop = () => {
    if (recorderRef.current?.state === "recording") {
      recorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const reset = () => {
    setBlob(null);
    setPreviewUrl("");
    setDuration(0);
    setError("");
    setState("idle");
    setUploadedInfo(null);
  };

  const upload = async () => {
    if (!blob) return;
    setState("uploading");
    setError("");
    try {
      const fd = new FormData();
      const ext = blob.type.includes("mp4") ? "mp4" : "webm";
      fd.append("file", new File([blob], `screencast.${ext}`, { type: blob.type }));
      fd.append("auto_generate", "true");
      const r = await api.post(`/academy/tutorials/${tutorialId}/recording`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const info = r.data || {};
      setUploadedInfo(info);
      setState("done");
      if (info.content_generated) {
        toast.success("Recording uploaded + tutorial script generated from transcript! 🎉");
      } else if (info.transcript_preview) {
        toast.message("Recording uploaded, but auto-generation didn't produce a full script.", {
          description: "You can still hit Generate script to try again.",
        });
      } else {
        toast.message("Recording uploaded. Transcription may still be running.");
      }
      onUploaded?.(info);
    } catch (e) {
      setState("preparing");   // let them retry without re-recording
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || e.message || "Upload failed");
      setError(msg);
      toast.error(msg);
    }
  };

  useEffect(() => () => {
    // Cleanup on unmount
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const durationStr = useMemo(() => {
    const m = Math.floor(duration / 60);
    const s = duration % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  }, [duration]);

  const existingUrl = existingRecording?.storage_path
    ? `${APP_ORIGIN}/api/academy/tutorials/${tutorialId}/recording`
    : "";

  return (
    <div className="rounded-md border border-line bg-bg-elevated p-4" data-testid="screen-recorder-panel">
      <div className="flex items-center gap-2 mb-3">
        <Video className="h-4 w-4 text-brand-amber" />
        <div className="type-list-title">Screen Recording Assistant</div>
        <span className="type-caption text-ink-muted ml-auto">Browser only · no upload while recording</span>
      </div>

      {!supported ? (
        <div className="rounded-md border border-yellow-500/40 bg-yellow-500/5 p-3 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-yellow-400" />
          <div className="type-caption text-yellow-200">
            Your browser doesn&apos;t support screen recording. Try Chrome, Edge, or a recent Firefox.
          </div>
        </div>
      ) : (
        <>
          {existingUrl && !previewUrl && state === "idle" && (
            <div className="mb-3 rounded-md border border-line bg-bg-surface p-3">
              <div className="type-caption text-ink-muted mb-2">Latest recording:</div>
              <video
                data-testid="existing-recording-video"
                src={existingUrl}
                controls
                className="w-full rounded"
                style={{ maxHeight: 240, background: "#000" }}
              />
              {existingRecording?.transcript && (
                <details className="mt-2">
                  <summary className="type-caption cursor-pointer text-ink-muted hover:text-brand-amber">
                    View transcript ({(existingRecording.transcript || "").length} chars)
                  </summary>
                  <pre className="mt-2 whitespace-pre-wrap text-xs bg-bg p-2 rounded max-h-40 overflow-y-auto">
                    {existingRecording.transcript}
                  </pre>
                </details>
              )}
            </div>
          )}

          {state === "idle" && (
            <div className="flex items-center gap-3 flex-wrap">
              <button
                data-testid="recorder-start-btn"
                onClick={start}
                className="inline-flex items-center gap-2 type-button-label px-4 py-2 rounded-md bg-brand-amber text-bg hover:opacity-90"
              >
                <Video className="h-4 w-4" /> {existingUrl ? "Record again" : "Start recording"}
              </button>
              <label className="inline-flex items-center gap-2 type-caption text-ink-secondary cursor-pointer">
                <input
                  type="checkbox"
                  checked={captureAudio}
                  onChange={(e) => setCaptureAudio(e.target.checked)}
                  className="accent-brand-amber"
                  data-testid="recorder-audio-toggle"
                />
                <span>Record my mic + system audio</span>
              </label>
              <span className="type-caption text-ink-muted ml-auto">
                We&apos;ll transcribe it and auto-write the tutorial script.
              </span>
            </div>
          )}

          {state === "recording" && (
            <div className="rounded-md border border-red-500/40 bg-red-500/5 p-3 flex items-center gap-3 flex-wrap">
              <div className="inline-flex items-center gap-2 text-red-300 font-mono type-list-title">
                <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                REC · {durationStr}
              </div>
              <button
                data-testid="recorder-stop-btn"
                onClick={stop}
                className="inline-flex items-center gap-2 type-button-label px-4 py-2 rounded-md border border-red-500/40 bg-red-500/10 text-red-300"
              >
                <Square className="h-4 w-4" /> Stop
              </button>
              <span className="type-caption text-ink-muted ml-auto">
                Recording your screen. Hit Stop when you&apos;re done.
              </span>
            </div>
          )}

          {(state === "preparing" || state === "uploading" || state === "done") && (
            <div className="space-y-3">
              {previewUrl && (
                <video
                  data-testid="recorder-preview"
                  src={previewUrl}
                  controls
                  className="w-full rounded"
                  style={{ maxHeight: 320, background: "#000" }}
                />
              )}
              <div className="flex items-center gap-2 flex-wrap">
                {state !== "done" && (
                  <button
                    data-testid="recorder-upload-btn"
                    onClick={upload}
                    disabled={state === "uploading"}
                    className="inline-flex items-center gap-2 type-button-label px-4 py-2 rounded-md bg-brand-amber text-bg disabled:opacity-50"
                  >
                    {state === "uploading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {state === "uploading" ? "Uploading + transcribing…" : "Upload + auto-generate tutorial"}
                  </button>
                )}
                <button
                  data-testid="recorder-reset-btn"
                  onClick={reset}
                  className="inline-flex items-center gap-2 type-caption px-3 py-2 rounded-md border border-line"
                >
                  <X className="h-3 w-3" /> Discard
                </button>
                <span className="type-caption text-ink-muted ml-auto">
                  {blob ? `${Math.round(blob.size / 1024 / 1024 * 10) / 10} MB · ${durationStr}` : ""}
                </span>
              </div>
              {uploadedInfo && (
                <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
                  <div className="inline-flex items-center gap-2 text-emerald-300 type-list-title mb-1">
                    <CheckCircle2 className="h-4 w-4" />
                    Uploaded &amp; transcribed
                  </div>
                  <div className="type-caption text-ink-secondary space-y-1">
                    <div>Language detected: <b>{(uploadedInfo.transcript_language || "en").toUpperCase()}</b></div>
                    {uploadedInfo.duration_s && (
                      <div>Duration: {Math.round(uploadedInfo.duration_s)}s</div>
                    )}
                    <div>Tutorial script auto-generated:{" "}
                      <b className={uploadedInfo.content_generated ? "text-emerald-300" : "text-yellow-300"}>
                        {uploadedInfo.content_generated ? "Yes ✓" : "Partial — try Generate script"}
                      </b>
                    </div>
                    {uploadedInfo.transcript_preview && (
                      <div className="italic text-ink-muted mt-1">
                        Preview: &ldquo;{uploadedInfo.transcript_preview.slice(0, 200)}…&rdquo;
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="mt-3 rounded-md border border-red-500/40 bg-red-500/5 p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-400 mt-0.5" />
              <div className="type-caption text-red-200">{error}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// 2. Sora 2 AI Video Generator
// ═══════════════════════════════════════════════════════════════════

const SORA_MODELS  = [{ id: "sora-2", label: "Sora 2 (faster)" }, { id: "sora-2-pro", label: "Sora 2 Pro (higher quality)" }];
const SORA_SIZES   = [
  { id: "1280x720",  label: "Widescreen HD (1280×720)" },
  { id: "1792x1024", label: "Ultra-wide (1792×1024)" },
  { id: "1024x1792", label: "Vertical / Reels (1024×1792)" },
  { id: "1024x1024", label: "Square (1024×1024)" },
];
const SORA_DURATIONS = [4, 8, 12];

export function SoraVideoGeneratorPanel({ tutorialId, existingVideo, onGenerated }) {
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("sora-2");
  const [size, setSize] = useState("1280x720");
  const [duration, setDuration] = useState(4);
  const [job, setJob] = useState(null);  // {job_id, status, video_url?, error?}
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  const poll = async (jobId) => {
    try {
      const r = await api.get(`/academy/tutorials/${tutorialId}/generate-video/${jobId}/status`);
      const d = r.data || {};
      setJob(d);
      if (d.status === "succeeded" || d.status === "failed") {
        if (d.status === "succeeded") {
          toast.success("Sora 2 finished your video 🎬");
          onGenerated?.(d);
        } else {
          toast.error(d.error || "Sora 2 job failed");
        }
        return;
      }
      pollRef.current = setTimeout(() => poll(jobId), 8000);
    } catch (e) {
      // Even 404 is possible if the row got dropped — stop the loop
      pollRef.current = null;
    }
  };

  const start = async () => {
    if (!prompt.trim() || prompt.trim().length < 8) {
      toast.error("Give Sora at least a full sentence to work with (min 8 chars).");
      return;
    }
    try {
      const r = await api.post(`/academy/tutorials/${tutorialId}/generate-video`, {
        prompt: prompt.trim(),
        model, size, duration,
      });
      const d = r.data || {};
      setJob({ job_id: d.job_id, status: "processing", estimated_seconds: d.estimated_seconds });
      toast.message(`Sora 2 is composing your ${duration}s clip…`, {
        description: `Typical wait: ${Math.round((d.estimated_seconds || 180) / 60)} min. We'll poll every 8s.`,
        duration: 6000,
      });
      poll(d.job_id);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Sora 2 job failed to start"));
    }
  };

  const isRunning = job && job.status === "processing";
  const isDone = job && job.status === "succeeded";
  const videoUrl = (isDone && job.video_url) ? `${APP_ORIGIN}${job.video_url}`
                  : (existingVideo?.video_url ? `${APP_ORIGIN}${existingVideo.video_url}` : "");

  return (
    <div className="rounded-md border border-line bg-bg-elevated p-4" data-testid="sora-panel">
      <div className="flex items-center gap-2 mb-3">
        <Film className="h-4 w-4 text-brand-amber" />
        <div className="type-list-title">AI Video Generator · Sora 2</div>
        <span className="type-caption text-ink-muted ml-auto">Uses your Universal Key</span>
      </div>

      <textarea
        data-testid="sora-prompt"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={3}
        maxLength={1200}
        placeholder="e.g. A DJ smoothly cross-fading two records at a warm sunset radio-studio bay, cinematic close-up, soft golden light."
        disabled={isRunning}
        className="w-full px-3 py-2 rounded-md bg-bg-surface border border-line text-ink-primary type-body focus:border-brand-amber focus:outline-none resize-none"
      />

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
        <label className="flex flex-col gap-1">
          <span className="type-caption uppercase tracking-wider text-ink-muted">Model</span>
          <select
            data-testid="sora-model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            disabled={isRunning}
            className="px-3 py-2 rounded-md bg-bg-surface border border-line text-ink-primary type-body"
          >
            {SORA_MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="type-caption uppercase tracking-wider text-ink-muted">Size</span>
          <select
            data-testid="sora-size"
            value={size}
            onChange={(e) => setSize(e.target.value)}
            disabled={isRunning}
            className="px-3 py-2 rounded-md bg-bg-surface border border-line text-ink-primary type-body"
          >
            {SORA_SIZES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="type-caption uppercase tracking-wider text-ink-muted">Duration</span>
          <select
            data-testid="sora-duration"
            value={duration}
            onChange={(e) => setDuration(parseInt(e.target.value, 10))}
            disabled={isRunning}
            className="px-3 py-2 rounded-md bg-bg-surface border border-line text-ink-primary type-body"
          >
            {SORA_DURATIONS.map((d) => <option key={d} value={d}>{d} seconds</option>)}
          </select>
        </label>
      </div>

      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <button
          data-testid="sora-generate-btn"
          onClick={start}
          disabled={isRunning || !prompt.trim()}
          className="inline-flex items-center gap-2 type-button-label px-4 py-2 rounded-md bg-brand-amber text-bg disabled:opacity-50"
        >
          {isRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {isRunning ? "Sora 2 is rendering…" : "Generate video"}
        </button>
        {isRunning && (
          <span className="type-caption text-ink-muted">
            Typical wait: 2–5 minutes. This box will update automatically.
          </span>
        )}
      </div>

      {videoUrl && (
        <div className="mt-4">
          <div className="type-caption text-ink-muted mb-1">
            {isDone ? "Just generated:" : "Latest video for this tutorial:"}
          </div>
          <video
            data-testid="sora-video"
            src={videoUrl}
            controls
            className="w-full rounded"
            style={{ maxHeight: 360, background: "#000" }}
          />
        </div>
      )}

      {job?.status === "failed" && (
        <div className="mt-3 rounded-md border border-red-500/40 bg-red-500/5 p-3 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-red-400 mt-0.5" />
          <div className="type-caption text-red-200">{job.error || "Video generation failed"}</div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// 3. Learning Paths
// ═══════════════════════════════════════════════════════════════════

export function LearningPathsPanel({ tutorials = [] }) {
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);   // hydrated path detail
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/academy/paths");
      setPaths(r.data?.paths || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadDetail = async (pid) => {
    try {
      const r = await api.get(`/academy/paths/${pid}`);
      setDetail(r.data);
      setExpanded(pid);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load path");
    }
  };

  const createPath = async () => {
    if (!newTitle.trim() || newTitle.trim().length < 3) {
      toast.error("Give the learning path a title first.");
      return;
    }
    setCreating(true);
    try {
      await api.post("/academy/paths", {
        title: newTitle.trim(),
        description: newDesc.trim() || undefined,
        tutorial_ids: [],
      });
      setNewTitle("");
      setNewDesc("");
      toast.success("Learning path created.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create path");
    } finally { setCreating(false); }
  };

  const removePath = async (pid) => {
    if (!await appConfirm("Delete this learning path? (Tutorials themselves stay.)")) return;
    try {
      await api.delete(`/academy/paths/${pid}`);
      toast.success("Path deleted.");
      if (expanded === pid) { setExpanded(null); setDetail(null); }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }  };

  const removeTutorial = async (pid, tid) => {
    if (!detail) return;
    const next = (detail.tutorial_ids || []).filter((x) => x !== tid);
    try {
      await api.patch(`/academy/paths/${pid}`, { tutorial_ids: next });
      await loadDetail(pid);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const openPicker = () => {
    setSelectedIds(new Set(detail?.tutorial_ids || []));
    setPickerOpen(true);
  };

  const savePicker = async () => {
    if (!detail) return;
    try {
      await api.patch(`/academy/paths/${detail.path_id}`, {
        tutorial_ids: Array.from(selectedIds),
      });
      setPickerOpen(false);
      await loadDetail(detail.path_id);
      await load();
      toast.success("Learning path updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const markComplete = async (pid, tid, completed = true) => {
    try {
      await api.post(`/academy/paths/${pid}/progress`, { tutorial_id: tid, completed });
      await loadDetail(pid);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save progress");
    }
  };

  // Update #179 · SCORM export for the whole path
  const [exportingPath, setExportingPath] = useState(null);   // path_id currently exporting
  const exportPathScorm = async (pid, title) => {
    if (exportingPath) return;
    setExportingPath(pid);
    toast.message("Building SCORM package for the path…");
    try {
      const resp = await api.post(
        `/academy/paths/${pid}/export/scorm`,
        {},
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `academy-path-${(title || pid).toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 60)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("SCORM package downloaded");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 400) toast.error("The path has no tutorials to export.");
      else toast.error(e?.response?.data?.detail || "SCORM export failed");
    } finally { setExportingPath(null); }
  };

  return (
    <section className="rounded-md border border-line bg-bg-elevated p-4" data-testid="learning-paths-panel">
      <div className="flex items-center gap-2 mb-3">
        <ListChecks className="h-4 w-4 text-brand-amber" />
        <div className="type-list-title">Learning Paths</div>
        <span className="type-caption text-ink-muted ml-auto">
          Group tutorials into ordered courses · track staff progress
        </span>
      </div>

      {/* Create form */}
      <div className="mb-4 rounded-md border border-line bg-bg-surface p-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="lp-new-title"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Path title — e.g. Morning Show Onboarding"
            className="flex-1 px-3 py-2 rounded-md bg-bg border border-line type-body focus:border-brand-amber focus:outline-none"
          />
          <button
            data-testid="lp-create-btn"
            onClick={createPath}
            disabled={creating || !newTitle.trim()}
            className="inline-flex items-center gap-2 type-button-label px-4 py-2 rounded-md bg-brand-amber text-bg disabled:opacity-50"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Create path
          </button>
        </div>
        <textarea
          data-testid="lp-new-desc"
          value={newDesc}
          onChange={(e) => setNewDesc(e.target.value)}
          rows={1}
          maxLength={1000}
          placeholder="Optional description — what will staff learn?"
          className="mt-2 w-full px-3 py-2 rounded-md bg-bg border border-line type-caption focus:border-brand-amber focus:outline-none resize-none"
        />
      </div>

      {/* Paths list */}
      {loading ? (
        <div className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin text-brand-amber mx-auto" /></div>
      ) : paths.length === 0 ? (
        <div className="text-center py-6 type-caption text-ink-muted" data-testid="lp-empty">
          No learning paths yet. Create one above.
        </div>
      ) : (
        <div className="space-y-2">
          {paths.map((p) => (
            <div key={p.path_id} className="rounded-md border border-line bg-bg-surface"
                 data-testid={`lp-row-${p.path_id}`}>
              <button
                onClick={() => (expanded === p.path_id ? (setExpanded(null), setDetail(null)) : loadDetail(p.path_id))}
                className="w-full text-left p-3 flex items-center gap-3 hover:bg-bg-elevated transition"
              >
                <GripVertical className="h-4 w-4 text-ink-muted" />
                <div className="flex-1 min-w-0">
                  <div className="type-list-title text-ink-primary truncate">{p.title}</div>
                  {p.description && <div className="type-caption text-ink-muted truncate">{p.description}</div>}
                </div>
                <span className="type-caption text-ink-muted">
                  {p.tutorial_count} tutorial{p.tutorial_count === 1 ? "" : "s"}
                </span>
                <ChevronRight className={`h-4 w-4 text-ink-muted transition ${expanded === p.path_id ? "rotate-90" : ""}`} />
              </button>

              {expanded === p.path_id && detail?.path_id === p.path_id && (
                <div className="border-t border-line p-3 space-y-3">
                  {/* Progress bar */}
                  <div>
                    <div className="flex justify-between type-caption text-ink-muted mb-1">
                      <span>Progress ({detail.progress?.completed_tutorial_ids?.length || 0} / {detail.tutorials?.length || 0})</span>
                      <span>{detail.progress?.percent || 0}%</span>
                    </div>
                    <div className="h-2 bg-bg rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-amber transition-all"
                        style={{ width: `${detail.progress?.percent || 0}%` }}
                        data-testid={`lp-progress-${p.path_id}`}
                      />
                    </div>
                  </div>

                  {/* Ordered tutorial list */}
                  {(detail.tutorials || []).length === 0 ? (
                    <div className="text-center py-4 type-caption text-ink-muted">
                      No tutorials added yet.
                    </div>
                  ) : (
                    <ol className="space-y-1.5">
                      {detail.tutorials.map((t, i) => {
                        const done = detail.progress?.completed_tutorial_ids?.includes(t.tutorial_id);
                        return (
                          <li key={t.tutorial_id}
                              data-testid={`lp-tut-${t.tutorial_id}`}
                              className="flex items-center gap-2 p-2 rounded-md border border-line bg-bg">
                            <span className="type-caption text-ink-muted w-5 text-right">{i + 1}.</span>
                            <button
                              onClick={() => markComplete(p.path_id, t.tutorial_id, !done)}
                              className={`h-5 w-5 rounded-full border grid place-items-center transition ${
                                done ? "border-emerald-400 bg-emerald-500/20" : "border-ink-muted"
                              }`}
                              title={done ? "Mark incomplete" : "Mark complete"}
                              data-testid={`lp-toggle-${t.tutorial_id}`}
                            >
                              {done && <CheckCircle2 className="h-3 w-3 text-emerald-400" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className={`type-body truncate ${done ? "line-through text-ink-muted" : "text-ink-primary"}`}>
                                {t.title || "(untitled)"}
                              </div>
                              <div className="type-caption text-ink-muted truncate">
                                {t.feature} · {t.duration_minutes} min
                              </div>
                            </div>
                            <button
                              onClick={() => removeTutorial(p.path_id, t.tutorial_id)}
                              className="p-1 rounded hover:bg-red-500/10 text-red-400"
                              title="Remove from path"
                              data-testid={`lp-remove-${t.tutorial_id}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </li>
                        );
                      })}
                    </ol>
                  )}

                  <div className="flex items-center gap-2">
                    <button
                      onClick={openPicker}
                      data-testid={`lp-pick-${p.path_id}`}
                      className="inline-flex items-center gap-1 type-caption px-3 py-1.5 rounded-md border border-brand-amber/30 bg-brand-amber/5 text-brand-amber"
                    >
                      <Plus className="h-3 w-3" /> Add / reorder tutorials
                    </button>
                    <button
                      onClick={() => exportPathScorm(p.path_id, p.title)}
                      disabled={exportingPath === p.path_id || !detail?.tutorial_ids?.length}
                      data-testid={`lp-scorm-${p.path_id}`}
                      title="Download a SCORM 1.2 ZIP — upload into Moodle, Docebo, TalentLMS, Canvas, Blackboard, Cornerstone, …"
                      className="inline-flex items-center gap-1 type-caption px-3 py-1.5 rounded-md border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 disabled:opacity-40 transition"
                    >
                      {exportingPath === p.path_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                      Export to LMS (SCORM)
                    </button>
                    <button
                      onClick={() => removePath(p.path_id)}
                      data-testid={`lp-delete-${p.path_id}`}
                      className="inline-flex items-center gap-1 type-caption px-3 py-1.5 rounded-md border border-red-500/30 text-red-300 ml-auto"
                    >
                      <Trash2 className="h-3 w-3" /> Delete path
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Tutorial picker modal */}
      {pickerOpen && detail && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)" }}
          onClick={() => setPickerOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-md border border-line bg-bg-elevated p-4 max-h-[80vh] overflow-y-auto"
            data-testid="lp-picker-modal"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="type-list-title">Add tutorials to path</div>
              <button onClick={() => setPickerOpen(false)}
                      className="h-7 w-7 rounded-full flex items-center justify-center hover:bg-bg">
                <X className="h-4 w-4" />
              </button>
            </div>
            {tutorials.length === 0 ? (
              <div className="text-center py-6 type-caption text-ink-muted">
                No tutorials in this station yet. Create some first.
              </div>
            ) : (
              <ul className="space-y-1">
                {tutorials.map((t) => {
                  const on = selectedIds.has(t.tutorial_id);
                  return (
                    <li key={t.tutorial_id}>
                      <label
                        className="flex items-center gap-2 p-2 rounded-md hover:bg-bg-surface cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => {
                            const next = new Set(selectedIds);
                            if (on) next.delete(t.tutorial_id); else next.add(t.tutorial_id);
                            setSelectedIds(next);
                          }}
                          className="accent-brand-amber"
                          data-testid={`lp-pick-tut-${t.tutorial_id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="type-body text-ink-primary truncate">{t.title || "(untitled)"}</div>
                          <div className="type-caption text-ink-muted truncate">
                            {t.feature} · {t.duration_minutes} min · {t.status}
                          </div>
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setPickerOpen(false)}
                      className="type-caption px-3 py-1.5 rounded-md border border-line">
                Cancel
              </button>
              <button onClick={savePicker}
                      data-testid="lp-picker-save"
                      className="type-button-label px-4 py-1.5 rounded-md bg-brand-amber text-bg">
                Save ({selectedIds.size})
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
